<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref, watch } from 'vue'

import { useSidebarStore } from '@/store/sidebars'
import { useAccountStore } from '@/store/account'
import { storeToRefs } from 'pinia'

import TtContainer from '@/components/TtContainer.vue'
import IconParkingPaid from '@/components/Icon/ParkingPaid.vue'
import IconLocations from '@/components/Icon/Locations.vue'
import IconToggleSecondary from '@/components/Icon/ToggleSecondary.vue'
import Bookings from '../SidebarSecondary/Bookings.vue'
import EditLocations from '../SidebarSecondary/EditLocations.vue'
import Payments from '../SidebarSecondary/Payments.vue'

const sidebarStore = useSidebarStore()
const { t } = useI18n({ useScope: 'global' })

const { hasRole } = storeToRefs(useAccountStore())
// Store the open state to a ref on the component
const { open, expanded, activeEditTab } = storeToRefs(sidebarStore)
// Add references to the elemnents that we want to scroll
const primaryPanelScrollElement = ref<undefined | HTMLElement>(undefined)

// Watch the open state and scroll the elements to the top if the open state is false (closing)
watch(open, () => {
  if (!open.value) {
    primaryPanelScrollElement.value?.scrollTo(0, 0)
  }
})

const tools = ref([
  {
    id: 'bookings',
    name: t('tools.bookings'),
    availableFor: ['siteowner'],
    icon: IconParkingPaid
  },
  {
    id: 'edit-locations',
    name: t('tools.my-locations'),
    availableFor: ['siteowner'],
    icon: IconLocations
  },
  // {
  //   id: 'invoices',
  //   name: t('tools.invoices'),
  //   availableFor: ['companyadmin'],
  //   icon: IconVerified
  // },
  // {
  //   id: 'payments',
  //   name: t('tools.payments'),
  //   availableFor: ['siteowner'],
  //   icon: IconVerified
  // },
  // {
  //   id: 'employees',
  //   name: t('tools.employees'),
  //   availableFor: ['companyadmin'],
  //   icon: IconVerified
  // }
])

const toggleTab = (id: string) => {
  activeEditTab.value = id
  if (!expanded.value) {
    sidebarStore.openExpanded()
  }
}
</script>

<template>
  <TtContainer variant="overlay-secondary" :open="open" :expanded="expanded">
    <div class="grid h-full grid-cols-[90px_1fr] gap-8">
      <div
        ref="primaryPanelScrollElement"
        :class="`${
          open ? 'overflow-auto pt-3.5' : 'overflow-hidden pt-2.5'
        } flex h-full flex-1 flex-col justify-between pb-10 pl-2.5 sm:w-full`"
      >
        <div
          class="flex w-full flex-col items-center justify-center bg-tt-pale"
        >
          <button @click="expanded = false" class="flex items-center h-10 py-px box-content">
            <IconToggleSecondary />
          </button>
          <div class="flex flex-col gap-1.5 mt-4">
            <template v-for="tool in tools">
              <button
                v-if="tool.availableFor.some((role) => hasRole(role))"
                :key="tool.name"
                @click="toggleTab(tool.id)"
                class="flex flex-col items-center gap-2 px-1 py-2.5 text-center text-sm leading-3 text-tt-gray hover:bg-tt-pale"
              >
                <component :is="tool.icon" class="h-6 w-6" v-if="tool.icon" />
                {{ tool.name }}
              </button>
            </template>
          </div>
        </div>

        <h2
          class="flex -rotate-90 transform flex-row gap-1 text-lg font-semibold leading-9 tracking-tight"
        >
          <span v-if="hasRole('siteowner')">{{ t('tools.siteowner') }}</span>
          <!-- <span v-if="hasRole('siteowner') && hasRole('companyadmin')">{{ '/' }}</span>
          <span v-if="hasRole('companyadmin')">{{ t('tools.companyadmin') }}</span> -->
        </h2>
      </div>

      <div class="overflow-x-auto pt-5 min-w-max">
        <Bookings v-if="activeEditTab === 'bookings' && hasRole('siteowner')" />
        <EditLocations
          v-if="activeEditTab === 'edit-locations' && hasRole('siteowner')"
        />
        <Payments v-if="activeEditTab === 'payments' && hasRole('siteowner')" />
      </div>
    </div>
  </TtContainer>
</template>
